/* ============================================================
 * src/screens/pending/_bundle.tsx
 *
 * Mechanical ESM transcription of the v.3 prototype's screens-pending.jsx.
 * The prototype destructured React from a global and exposed each screen
 * via Object.assign(window, ...). This file restores the same surface as
 * named ESM exports; per-component files in this directory re-export the
 * screen the route table maps to so downstream wiring tasks can edit a
 * single component without touching the bundle.
 * ============================================================ */
/* eslint-disable react-refresh/only-export-components, @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Icon, Btn, Pill, CircaTag, Panel, PageHead, Breadcrumbs, Tabs, Spark,
  StatCard, ScoreRing, HelpDot, TimerChip, AccessGate, AdminGate, CapPill,
  FreshnessBanner, AuditTrailLink, Field, Input, Textarea, Select, Toggle,
  SegmentedControl, Thumb, Avatar, EmptyState, Skeleton, useLoading,
  SkeletonStatRow, SkeletonCardGrid, SkeletonTable, SkeletonPanel, SkeletonList,
  Modal, SlideOver, ConfirmModal, Dropdown, Progress, TONES,
  useToast, useMutationWithToast,
} from '../../components/primitives';
// T318 — the mock fixture import retired; the asset-player permission gate
// reads the role-mirror via the canonical `can()` helper (lib/permissions),
// fed by the real `useCurrentMember()` hook. No fixture dependency remains.
import { can as canPerm } from '../../lib/permissions';
import { useCurrentMember } from '../../hooks/useCurrentMember';
import { fmtClock } from '../../lib/format';
import { navigate } from '../../lib/nav';
import Hls from 'hls.js';
import { Provenance, ProvenanceBadge } from '../../components/Provenance';

// Silence unused-import lint for primitives this file may not reference.
void React; void useState; void useEffect; void useRef; void useCallback; void useMemo;
void Icon; void Btn; void Pill; void CircaTag; void Panel; void PageHead; void Breadcrumbs;
void Tabs; void Spark; void StatCard; void ScoreRing; void HelpDot; void TimerChip;
void AccessGate; void AdminGate; void CapPill; void FreshnessBanner; void AuditTrailLink;
void Field; void Input; void Textarea; void Select; void Toggle; void SegmentedControl;
void Thumb; void Avatar; void EmptyState; void Skeleton; void useLoading;
void SkeletonStatRow; void SkeletonCardGrid; void SkeletonTable; void SkeletonPanel; void SkeletonList;
void Modal; void SlideOver; void ConfirmModal; void Dropdown; void Progress; void TONES;
void useToast; void useMutationWithToast;
void navigate;

/* ============================================================
   CIRCA — H4 Asset player primitive (+ sprite hover, QoE)
            H5 Poster / thumbnail generation
   One headless player for audio & video; real working transport,
   sprite-hover scrubber, live quality readout (never zeros).
   Deep-green; Spanish; honest states.
   ============================================================ */

/* useSpriteSheet — deterministic sprite tiles for hover preview (interval ≥ 1s) */
export function useSpriteSheet(assetId, durationMs, interval = 2000) {
  return useMemo(() => {
    const n = Math.max(1, Math.min(48, Math.floor(durationMs / interval)));
    const seed = (assetId || 'x').split('').reduce((s, c) => s + c.charCodeAt(0), 0);
    const tiles = Array.from({ length: n }, (_, i) => {
      const h = (seed * 7 + i * 37) % 360;
      return `linear-gradient(135deg, hsl(${h} 32% 26%), hsl(${(h + 40) % 360} 38% 16%))`;
    });
    return { count: n, interval, tileAt: (ms) => tiles[Math.min(n - 1, Math.floor(ms / interval))] };
  }, [assetId, durationMs, interval]);
}

/* AssetPlayer — real audio/video player behind the CIRCA chrome.
 *
 * Same UI as the prototype (poster box, overlay play button, custom transport
 * with sprite-hover scrubber, live QoE pills) but wired to a real <video>/
 * <audio> element. HLS sources (.m3u8) play through a self-hosted hls.js in
 * browsers without native HLS (Chrome/Firefox) and through the built-in player
 * in Safari/iOS; progressive mp3/mp4 enclosures attach directly. Transport,
 * clock, duration, mute and QoE are all driven from real media events. */
export function AssetPlayer({ descriptor }) {
  const d = descriptor || {};
  const isAudio = d.mediaKind === 'audio';
  const srcUrl = d.source?.url;
  const isHls = !!srcUrl && (/\.m3u8(\?|$)/i.test(srcUrl) || (d.source?.contentType || '').toLowerCase().includes('mpegurl'));

  const mediaRef = useRef(null); // the real <video>/<audio> element
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0); // ms
  const [durMs, setDurMs] = useState(d.durationMs || 0); // 0 = unknown until metadata
  const [hover, setHover] = useState(null); // {ms, x}
  const [captions, setCaptions] = useState(!!(d.textTracks && d.textTracks.length));
  const [muted, setMuted] = useState(false);
  const [boxHover, setBoxHover] = useState(false);
  const dur = durMs || d.durationMs || 0;
  const sprite = useSpriteSheet(d.assetId, dur || 1, 2000);
  const railRef = useRef(null);

  const [qoe, setQoe] = useState({ buffer: 0, rebuffers: 0, bitrate: isAudio ? 256 : 0 });

  // Attach the source — hls.js for HLS where supported, native otherwise.
  // Re-runs (and tears down) whenever the source URL changes.
  useEffect(() => {
    const video = mediaRef.current;
    if (!video || !srcUrl) return;
    setPlaying(false); setPos(0); setDurMs(d.durationMs || 0);
    setQoe({ buffer: 0, rebuffers: 0, bitrate: isAudio ? 256 : 0 });

    if (isHls && Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true });
      hls.loadSource(srcUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.LEVEL_SWITCHED, (_e, data) => {
        const lvl = hls.levels?.[data.level];
        if (lvl?.bitrate) setQoe(q => ({ ...q, bitrate: Math.round(lvl.bitrate / 1000) }));
      });
      // Single-rendition media playlists carry no level bitrate; derive the
      // real rate from each buffered fragment (bytes * 8 / seconds).
      hls.on(Hls.Events.FRAG_BUFFERED, (_e, data) => {
        const bytes = data?.frag?.stats?.total ?? data?.stats?.total;
        const secs = data?.frag?.duration;
        if (bytes && secs) setQoe(q => ({ ...q, bitrate: Math.round((bytes * 8) / secs / 1000) }));
      });
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (data.fatal) console.error('[AssetPlayer] hls.js fatal', data.type, data.details);
      });
      return () => hls.destroy();
    }
    // Native: Safari/iOS HLS, or progressive mp3/mp4.
    video.src = srcUrl;
    return () => { video.removeAttribute('src'); video.load?.(); };
  }, [srcUrl, isHls, isAudio, d.durationMs]);

  // Drive transport state from real media element events.
  useEffect(() => {
    const video = mediaRef.current;
    if (!video) return;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onTime = () => {
      setPos(video.currentTime * 1000);
      // Buffer health: seconds buffered ahead of the playhead, mapped to a
      // 0–100 readout (30s lookahead == full).
      try {
        const b = video.buffered;
        let ahead = 0;
        for (let i = 0; i < b.length; i++) {
          if (b.start(i) <= video.currentTime && video.currentTime <= b.end(i)) { ahead = b.end(i) - video.currentTime; break; }
        }
        setQoe(q => ({ ...q, buffer: Math.min(100, Math.round((ahead / 30) * 100)) }));
      } catch { /* buffered can throw before metadata */ }
    };
    const onMeta = () => { if (isFinite(video.duration) && video.duration > 0) setDurMs(video.duration * 1000); };
    const onWaiting = () => setQoe(q => ({ ...q, rebuffers: q.rebuffers + 1 }));
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('loadedmetadata', onMeta);
    video.addEventListener('waiting', onWaiting);
    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('loadedmetadata', onMeta);
      video.removeEventListener('waiting', onWaiting);
    };
  }, []);

  useEffect(() => { if (mediaRef.current) mediaRef.current.muted = muted; }, [muted]);

  const togglePlay = useCallback(() => {
    const v = mediaRef.current;
    if (!v) return;
    if (v.paused) { const p = v.play(); if (p && p.catch) p.catch(() => {}); } else { v.pause(); }
  }, []);
  const seekTo = useCallback((ms) => {
    const v = mediaRef.current;
    if (v && dur > 0) { v.currentTime = Math.max(0, Math.min(dur, ms)) / 1000; setPos(Math.max(0, Math.min(dur, ms))); }
  }, [dur]);

  const pct = dur > 0 ? (pos / dur) * 100 : 0;
  const seekFromEvent = (e) => {
    const r = railRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
    return ratio * dur;
  };
  const onRailMove = (e) => { const r = railRef.current.getBoundingClientRect(); setHover({ ms: seekFromEvent(e), x: e.clientX - r.left }); };

  const Transport = ({ dark }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
      <button onClick={togglePlay} aria-label={playing ? 'Pausar' : 'Reproducir'} style={{ width: 44, height: 44, borderRadius: '50%', border: 'none', cursor: 'pointer', flexShrink: 0,
        background: dark ? '#fff' : 'var(--deep-green)', color: dark ? 'var(--ink)' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon name={playing ? 'pause' : 'play'} size={18} color={dark ? 'var(--ink)' : '#fff'} />
      </button>
      <span className="t-mono" style={{ fontSize: 12, color: dark ? 'rgba(255,255,255,0.85)' : 'var(--body-muted)', textTransform: 'none', width: 56, flexShrink: 0 }}>{fmtClock(pos)}</span>
      <div ref={railRef} onMouseMove={onRailMove} onMouseLeave={() => setHover(null)} onClick={e => seekTo(seekFromEvent(e))}
        style={{ position: 'relative', flex: 1, height: 6, borderRadius: 999, background: dark ? 'rgba(255,255,255,0.22)' : 'var(--lightest-gray)', cursor: 'pointer' }}>
        <div style={{ position: 'absolute', inset: 0, width: pct + '%', background: dark ? '#fff' : 'var(--deep-green)', borderRadius: 999 }} />
        <div style={{ position: 'absolute', top: '50%', left: pct + '%', transform: 'translate(-50%,-50%)', width: 13, height: 13, borderRadius: '50%', background: '#fff', border: '2px solid var(--deep-green)', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
        {/* sprite hover preview */}
        {hover && dur > 0 && (
          <div style={{ position: 'absolute', bottom: 'calc(100% + 10px)', left: Math.max(40, Math.min(hover.x, (railRef.current ? railRef.current.offsetWidth : 200) - 40)), transform: 'translateX(-50%)', pointerEvents: 'none', zIndex: 5 }}>
            <div style={{ position: 'relative', width: 96, height: 54, borderRadius: 8, background: sprite.tileAt(hover.ms), border: '2px solid #fff', boxShadow: '0 4px 14px rgba(0,0,0,0.3)' }}>
              <div style={{ position: 'absolute', top: 3, left: 3 }}>
                <ProvenanceBadge kind="mock" detail="Miniatura de scrubber simulada — gradiente determinista, no un fotograma real del video." />
              </div>
            </div>
            <div className="t-mono" style={{ fontSize: 10, color: '#fff', background: 'rgba(0,0,0,0.75)', borderRadius: 4, padding: '2px 5px', textAlign: 'center', marginTop: 4, textTransform: 'none' }}>{fmtClock(hover.ms)}</div>
          </div>
        )}
      </div>
      <span className="t-mono" style={{ fontSize: 12, color: dark ? 'rgba(255,255,255,0.6)' : 'var(--muted-slate)', textTransform: 'none', width: 56, flexShrink: 0, textAlign: 'right' }}>{fmtClock(dur)}</span>
      {d.textTracks && d.textTracks.length > 0 && (
        <button onClick={() => setCaptions(c => !c)} aria-label="Subtítulos" title="Subtítulos" style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 4, color: dark ? (captions ? '#fff' : 'rgba(255,255,255,0.5)') : (captions ? 'var(--deep-green)' : 'var(--muted-slate)') }}><Icon name="message" size={16} /></button>
      )}
      <button onClick={() => setMuted(m => !m)} aria-label="Silenciar" style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 4, color: dark ? 'rgba(255,255,255,0.7)' : 'var(--muted-slate)' }}><Icon name={muted ? 'x' : 'signal'} size={16} /></button>
    </div>
  );

  return (
    <div>
      {isAudio ? (
        <div style={{ borderRadius: 18, padding: 18, background: 'var(--snow)', border: '1px solid var(--border-cool)' }}>
          <audio ref={mediaRef} crossOrigin="anonymous" playsInline style={{ display: 'none' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
            <div style={{ width: 56, height: 56, borderRadius: 12, background: 'linear-gradient(135deg, var(--forest-a), var(--forest-b))', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="mic" size={24} color="#fff" /></div>
            <div style={{ minWidth: 0 }}><div style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.title || 'Pista de audio'}</div><div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>AUDIO · {qoe.bitrate} KBPS</div></div>
          </div>
          <Transport />
        </div>
      ) : (
        <div style={{ borderRadius: 18, overflow: 'hidden', background: 'var(--ink)', position: 'relative' }}>
          <div
            onMouseEnter={() => setBoxHover(true)} onMouseLeave={() => setBoxHover(false)}
            style={{ aspectRatio: '16/9', position: 'relative', background: d.posterUrl ? `center/cover url(${d.posterUrl})` : 'repeating-linear-gradient(135deg, #15201c, #15201c 16px, #182420 16px, #182420 32px)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <video
              ref={mediaRef}
              crossOrigin="anonymous"
              playsInline
              poster={d.posterUrl || undefined}
              onClick={togglePlay}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}
            >
              {(d.textTracks || []).map((t, i) => (
                <track key={`${t.src || i}`} src={t.src} kind={t.kind || 'subtitles'} label={t.label} srcLang={t.language || t.lang} default={i === 0 && captions} />
              ))}
            </video>
            {(!playing || boxHover) && (
              <button onClick={togglePlay} aria-label={playing ? 'Pausar' : 'Reproducir'} style={{ position: 'relative', zIndex: 2, width: 64, height: 64, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)' }}>
                <Icon name={playing ? 'pause' : 'play'} size={26} color="#fff" />
              </button>
            )}
          </div>
          <div style={{ padding: '12px 16px', background: 'var(--ink)' }}><Transport dark /></div>
        </div>
      )}
      {/* Live quality readout — real values from the media element / hls.js */}
      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <Pill tone={qoe.buffer >= 85 ? 'good' : qoe.buffer >= 50 ? 'warn' : 'bad'} size="sm" icon="gauge">Búfer {Math.round(qoe.buffer)}%</Pill>
        <Pill tone={qoe.rebuffers === 0 ? 'good' : qoe.rebuffers < 3 ? 'warn' : 'bad'} size="sm" icon="activity">{qoe.rebuffers} rebúfer{qoe.rebuffers === 1 ? '' : 's'}</Pill>
        <Pill tone="neutral" size="sm" icon="signal">{qoe.bitrate} kbps</Pill>
        <Provenance kind="local" detail="Búfer, rebúferes y bitrate medidos del elemento <video> de esta vista previa — NO es la telemetría de QoE del backend (QoeBridge → /qoe/beacon)." style={{ alignSelf: 'center' }}>
          <span style={{ fontSize: 11, color: 'var(--muted-slate)' }}>Telemetría de calidad (vista previa local)</span>
        </Provenance>
      </div>
    </div>
  );
}

/* Build a descriptor from a CIRCA asset */
export function descriptorFor(a, kind) {
  return {
    assetId: a.id,
    mediaKind: kind || 'video',
    source: { url: `https://media.circa.tv/v/${a.id}/${kind === 'audio' ? 'audio.mp3' : 'video.m3u8'}`, contentType: kind === 'audio' ? 'audio/mpeg' : 'application/x-mpegURL' },
    title: a.title,
    posterUrl: a._poster || null,
    durationMs: a.durationMs,
    textTracks: a.status === 'ready' ? [{ lang: 'es', label: 'Español (automático)' }] : [],
  };
}

/* ---------- H5 — Poster / thumbnail generation ---------- */
export function PosterGenerator({ asset, hasPoster, onGenerated, capabilityOff }) {
  const { toast } = useToast();
  const member = useCurrentMember();
  const [state, setState] = useState(hasPoster ? 'has' : 'none'); // none | working | has
  const generate = () => {
    if (capabilityOff || !canPerm('media_transform_invoke', member?.role)) return;
    setState('working');
    toast({ variant: 'info', message: 'Generando miniatura…' });
    setTimeout(() => { setState('has'); onGenerated && onGenerated(); toast({ variant: 'success', message: 'Miniatura generada.' }); }, 2200);
  };

  if (state === 'has') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, background: 'var(--snow)', borderRadius: 14 }}>
        <div style={{ width: 64, height: 36, borderRadius: 8, background: 'linear-gradient(135deg, var(--forest-b), var(--forest-a))', flexShrink: 0 }} />
        <div style={{ flex: 1 }}><div style={{ fontSize: 14, fontWeight: 500 }}>Miniatura lista</div><div style={{ fontSize: 12, color: 'var(--muted-slate)' }}>Generada a partir de un fotograma del video.</div></div>
        <AccessGate perm="media_transform_invoke"><Btn size="sm" variant="ghost" decorative icon="refresh" onClick={generate}>Regenerar</Btn></AccessGate>
      </div>
    );
  }

  if (capabilityOff) {
    return (
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: 16, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 14 }}>
        <Icon name="lock" size={18} color="var(--warn)" style={{ marginTop: 1 }} />
        <div style={{ flex: 1 }}><div style={{ fontWeight: 500, marginBottom: 3 }}>Generación de miniaturas en pausa</div><p style={{ margin: 0, fontSize: 13, color: 'var(--body-muted)' }}>Esta capacidad está temporalmente deshabilitada en el espacio de trabajo. Inténtalo más tarde.</p></div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: 16, border: '1px dashed var(--border-cool)', borderRadius: 14 }}>
      <div style={{ width: 64, height: 36, borderRadius: 8, background: 'var(--lightest-gray)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {state === 'working' ? <Icon name="loader" size={16} color="var(--muted-slate)" style={{ animation: 'circa-spin 0.8s linear infinite' }} /> : <Icon name="grid" size={16} color="var(--muted-slate)" />}
      </div>
      <div style={{ flex: 1 }}><div style={{ fontSize: 14, fontWeight: 500 }}>{state === 'working' ? 'Generando miniatura…' : 'Sin miniatura'}</div><div style={{ fontSize: 12, color: 'var(--muted-slate)' }}>{state === 'working' ? 'Tomando un fotograma representativo.' : 'Crea una miniatura pulida automáticamente.'}</div></div>
      {state !== 'working' && <AccessGate perm="media_transform_invoke"><Btn size="sm" variant="solid" decorative icon="sparkles" onClick={generate}>Generar miniatura</Btn></AccessGate>}
    </div>
  );
}

